import { useState } from 'react';
import { adminApi } from '@/api/endpoints';
import type { SyncResult } from '@/api/types';
import { Alert, Button, Card, Notice } from '@/components/ui';
import styles from './admin.module.css';

/**
 * Sammelt alle Zähler aus der Sync-Antwort (§12.1: counts + leagues). Ein
 * Liga-Eintrag ist ein Zähler-OBJEKT ({teams, matchdays, matches, matched}) —
 * daraus wird die Spiele-Zahl gezogen; direkt rendern würde React #31 werfen.
 */
function collectCounts(res: SyncResult): [string, number][] {
  const out: Record<string, number> = { ...(res.counts ?? {}) };
  for (const [league, value] of Object.entries(res.leagues ?? {})) {
    out[league] = typeof value === 'number' ? value : (value.matches ?? value.matched ?? 0);
  }
  if (typeof res.synced === 'number') out.synced = res.synced;
  return Object.entries(out);
}

const COUNT_LABELS: Record<string, string> = {
  synced: 'Spiele synchronisiert',
  matches: 'Spiele',
  fixtures: 'Spiele',
  matchdays: 'Spieltage',
  teams: 'Teams',
  bl2: '2. Bundesliga',
  bl3: '3. Liga',
  dfb: 'DFB-Pokal',
};

export function AdminSyncPage() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSync() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminApi.syncFixtures();
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync fehlgeschlagen.');
    } finally {
      setRunning(false);
    }
  }

  const counts = result ? collectCounts(result) : [];

  return (
    <Card>
      <h2>Datenabgleich</h2>
      <p className="muted">
        Holt die Münster-Spiele (3. Liga &amp; DFB-Pokal) von OpenLigaDB in die Datenbank.
        Ohne API-Schlüssel. Jedes Spiel wird zu einem Spieltag mit genau einem Spiel; bereits
        manuell gesetzte Deadlines bleiben erhalten.
      </p>

      {error && <Alert>{error}</Alert>}

      <Button onClick={() => void handleSync()} disabled={running}>
        {running ? 'Synchronisierung läuft…' : 'Münster-Spiele synchronisieren'}
      </Button>

      {result && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            {result.message ??
              `Synchronisierung abgeschlossen${
                result.season ? ` (Saison ${result.season})` : ''
              }.`}
          </Notice>
          {counts.length > 0 && (
            <div className={styles.counts}>
              {counts.map(([key, value]) => (
                <span key={key}>
                  <strong>{value}</strong> {COUNT_LABELS[key] ?? key}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
